/**
 * DÖNEMSEL ANALİZ (kullanıcı, 13.10.2026).
 *
 * Üstte açılıp kapanan bölge: merkezin izlediği bütün varlıklar (`/tracked`),
 * aranır, türe göre gruplu, çoklu seçilir (en çok 10). Ortada dönem / tarih
 * aralığı, raporlama para birimi, getiri periyodu, temettü seçeneği ve sepet.
 * Altta grafik: seçilenlerin KAYAN PERİYOT getirisi — her gün, o günde biten
 * N günlük yüzde değişim.
 *
 * SEPET: istenirse ağırlıklar elle girilir ya da eşit dağıtılır; toplam tam
 * %100 olmadan çizilmez. Sepet istenmiyorsa oran sorulmaz. "Yalnızca sepet"
 * tekil çizgileri ve satırları gizler.
 *
 * HESAP CİHAZDA (kullanıcı kararı 13.10.2026): sunucudan yalnızca fiyat, kur
 * ve şirket olayı istenir (bugünkü kapılar); seçim ve oranlar cihazdan
 * çıkmaz. Fiyatlar profilin şifreli arşivine yazılır; aynı aralık ikinci
 * kez indirilmez. Oran ya da görünüm değişince yalnızca yeniden hesaplanır.
 *
 * RENK bir TANIMA rengidir, yargı değil: kırmızı/yeşil/sarı yok (hesap
 * renklerinin kapalı listesi). Sepet metin renginde, öbürleriyle AYNI
 * kalınlıkta (kullanıcı, 14.10.2026: kalın sepet çirkin duruyordu).
 *
 * VERİ YOKSA ÇİZGİ YOK: ölçülemeyen gün boşluk olarak kalır, seçili gün
 * panelinde sebebiyle yazar; sıfır çizilmez.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import type { CurrencyCode, IsoDate } from "@parafon/contracts";
import { isKnownCurrency } from "@parafon/contracts";
import type { TrackedListItemDto } from "@parafon/contracts/central-api";
import {
  DONEMSEL_MAX_GUN,
  DONEMSEL_MAX_ORTALAMA_GUN,
  DONEMSEL_MAX_PERIYOT_GUN,
  DONEMSEL_MIN_ORTALAMA_GUN,
  KUR_KIYASLARI,
  KUR_KIYAS_ONEKI,
  ortalamaGeriGun,
  type Absence,
  type DonemselAnalizIstegi,
  type DonemselAnalizSonucu,
  type DonemselDeger,
  type DonemselSeri,
  type SepetDagilimi,
  type SepetDengeleme,
} from "@parafon/contracts/local-api";
import type { FxSyncResult, SyncReport } from "@parafon/contracts/device-api";
import { useCentral } from "../central/CentralContext";
import { esitlemeninDegisimi } from "../central/EsitlemeSaglayici";
import { decimalSign, formatDecimal, formatRatio } from "../lib/decimal";
import { darEkranMi } from "../lib/grafikTercihleri";
import { assetClassLabel, formatIsoDate } from "../lib/format";
import { bugunYerelIso, type DonemKodu } from "../lib/tarihDonemleri";
import { fiyatlarDegisti } from "../lib/seriOnbellegi";
import {
  DONEMSEL_TERCIH_ANAHTARI,
  PERIYOTLAR,
  donemselTercihOku,
  donemselTercihYaz,
  type DonemselTercih,
} from "../lib/donemselTercih";
import { YUZ, esitDagit, kesirMetni, kesirdenYuzde, toplam, yuzdeOku, yuzdeYaz, yuzeOlcekle } from "../lib/sepetAgirlik";
import { HesapAdi } from "../ui/HesapAdi";
import { YakinlastirmaDugmeleri } from "../ui/YakinlastirmaDugmeleri";
import { useYakinlastirma, type Yakinlastirma } from "../lib/yakinlastirma";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { CurrencySelect } from "../ui/CurrencySelect";
import { Button, EmptyState, ErrorBlock, Field, Notice, Screen, Select, Spinner, TextInput, cx } from "../ui/primitives";
import { TarihAraligiSecici } from "../ui/TarihAraligiSecici";
import { gunEkle, guzelAdim, kilavuzDegerleri, YUKSEKLIK } from "./GrafikScreen";
import { useSinirlar } from "../kabiliyet/KabiliyetSaglayici";
import { KilitliKabiliyet } from "../kabiliyet/KilitliKabiliyet";
import { enEskiGun } from "../kabiliyet/kabiliyet";
import { donemselKurali, etkinDonemselTercih, secilebilir, type DonemselKural } from "../kabiliyet/donemselSinir";
import { M, YEREL } from "../dil";
import { errorText } from "../lib/rpcError";
import { motorMetni, motorMetniVeya } from "../dil/motorMetni";

/** Tanıma renkleri: hesap renklerinin kapalı listesi; 9. ve 10. kesikli. */
export const CIZGI_RENKLERI: readonly { readonly renk: string; readonly kesikli: boolean }[] = [
  { renk: "var(--pf-hesap-mavi)", kesikli: false },
  { renk: "var(--pf-hesap-eflatun)", kesikli: false },
  { renk: "var(--pf-hesap-turkuaz)", kesikli: false },
  { renk: "var(--pf-hesap-kahverengi)", kesikli: false },
  { renk: "var(--pf-hesap-mor)", kesikli: false },
  { renk: "var(--pf-hesap-petrol)", kesikli: false },
  { renk: "var(--pf-hesap-lacivert)", kesikli: false },
  { renk: "var(--pf-hesap-gri)", kesikli: false },
  { renk: "var(--pf-hesap-mavi)", kesikli: true },
  { renk: "var(--pf-hesap-eflatun)", kesikli: true },
];
const SEPET_RENGI = "var(--pf-text)";

const DENGELEMELER: readonly { readonly value: SepetDengeleme; readonly label: string }[] = [
  { value: "yok", label: M.donemsel.dengeleme.yok },
  { value: "aylik", label: M.donemsel.dengeleme.aylik },
  { value: "ceyreklik", label: M.donemsel.dengeleme.ceyreklik },
  { value: "yillik", label: M.donemsel.dengeleme.yillik },
];

/** Döviz kıyasları — liste öğesi biçiminde (künye haritası ve gruplama için). */
const KUR_KIYAS_OGELERI: readonly TrackedListItemDto[] = KUR_KIYASLARI.map((k) => ({
  instrumentId: `${KUR_KIYAS_ONEKI}${k.para}`,
  symbol: `${k.para}/TL`,
  exchange: "TCMB",
  assetClass: "fx",
  currency: k.para,
  name: M.ortak.kurKiyasAdi[k.para] ?? k.ad,
  state: "ready",
  horizonFrom: "2016-01-01",
  horizonLimitedBy: "target",
  backfilledTo: null,
  kiyas: true,
}));

const KIYAS_GRUBU = "kiyas";

/** Kıyas serisi mi: döviz kıyası (cihazın kurundan) ya da merkezin kıyas aracı. */
function kiyasMi(id: string, kunye: ReadonlyMap<string, TrackedListItemDto>): boolean {
  return id.startsWith(KUR_KIYAS_ONEKI) || kunye.get(id)?.kiyas === true;
}

/**
 * SEPETİN ÜYELERİ: seçilenlerden kıyas serileri DIŞINDA kalanlar (kullanıcı,
 * 14.10.2026: kıyas serisi sepete girmez — onunla KIYASLANIR).
 */
export function sepetUyeleri(
  secili: readonly string[],
  kunye: ReadonlyMap<string, TrackedListItemDto>,
): readonly string[] {
  return secili.filter((id) => !kiyasMi(id, kunye));
}

/** Hazır olmayan durumlar: geçmiş henüz dolmadı, seçilemez. */
const HAZIR_DURUMLAR = new Set(["ready", "dormant"]);

function gunFarki(from: IsoDate, to: IsoDate): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

function mesaj(err: unknown): string {
  return errorText(err);
}

type Durum =
  | { readonly tur: "bos" }
  | { readonly tur: "calisiyor"; readonly adim: "esitleniyor" | "hesaplaniyor" }
  | { readonly tur: "hata"; readonly hata: unknown };

interface Sonuc {
  readonly anahtar: string;
  readonly veri: DonemselAnalizSonucu;
  readonly uyarilar: readonly string[];
}

export function DonemselAnalizScreen() {
  const { repo, revision } = useRepositoryContext();
  const merkez = useCentral();
  const bugun = bugunYerelIso();

  // --- tercihler (şifreli kapta) -------------------------------------------
  const [tercihHam, setTercih] = useState<DonemselTercih | null>(null);
  useEffect(() => {
    let iptal = false;
    void repo.settings
      .get(DONEMSEL_TERCIH_ANAHTARI)
      .catch(() => null)
      .then((m) => {
        if (!iptal) setTercih(donemselTercihOku(m, bugunYerelIso()));
      });
    return () => {
      iptal = true;
    };
  }, [repo]);
  const degistir = useCallback(
    (yeni: Partial<DonemselTercih>) =>
      setTercih((t) => {
        if (t === null) return t;
        const s = { ...t, ...yeni };
        void repo.settings.set(DONEMSEL_TERCIH_ANAHTARI, donemselTercihYaz(s)).catch(() => undefined);
        return s;
      }),
    [repo],
  );

  // --- izlenen liste ---------------------------------------------------------
  const [liste, setListe] = useState<
    | { readonly durum: "yukleniyor" }
    | { readonly durum: "tamam"; readonly items: readonly TrackedListItemDto[] }
    | { readonly durum: "hata"; readonly hata: unknown }
  >({ durum: "yukleniyor" });
  const [listeYeniden, setListeYeniden] = useState(0);
  useEffect(() => {
    if (merkez.central === null) return;
    let iptal = false;
    setListe({ durum: "yukleniyor" });
    merkez.central
      .trackedList()
      .then((r) => {
        if (!iptal) setListe({ durum: "tamam", items: r.items });
      })
      .catch((hata: unknown) => {
        if (!iptal) setListe({ durum: "hata", hata });
      });
    return () => {
      iptal = true;
    };
  }, [merkez.central, listeYeniden]);
  // Döviz kıyasları sunucuda araç değil; listeye cihaz ekler (kur önbelleği).
  const listeKiyasli = useMemo(
    () =>
      liste.durum === "tamam"
        ? { durum: "tamam" as const, items: [...KUR_KIYAS_OGELERI, ...liste.items] }
        : liste,
    [liste],
  );
  // --- gelir modeli: kip, kendi varlıklar, en eski gün ----------------------
  const sinir = useSinirlar();
  const [kendi, setKendi] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (sinir.donemsel !== "kendi") return;
    let iptal = false;
    void repo
      .positions()
      .then((ps) => {
        if (iptal) return;
        setKendi(
          new Set(
            ps
              .filter((p) => p.asset.kind === "tracked" && decimalSign(p.quantity) > 0)
              .map((p) => (p.asset.kind === "tracked" ? p.asset.instrumentId : "")),
          ),
        );
      })
      .catch(() => undefined);
    return () => {
      iptal = true;
    };
  }, [repo, revision, sinir.donemsel]);
  const kural = useMemo(() => donemselKurali(sinir.donemsel, kendi), [sinir.donemsel, kendi]);
  const enEski = enEskiGun(sinir.fiyatGecmisiYil, bugun);

  const kunye = useMemo(() => {
    const m = new Map<string, TrackedListItemDto>();
    for (const i of KUR_KIYAS_OGELERI) m.set(i.instrumentId, i);
    if (liste.durum === "tamam") for (const i of liste.items) m.set(i.instrumentId, i);
    return m;
  }, [liste]);
  const etkin = useMemo(
    () =>
      tercihHam === null ? null : etkinDonemselTercih(tercihHam, kural, (id) => kiyasMi(id, kunye), enEski),
    [tercihHam, kural, kunye, enEski],
  );
  const tercih = etkin?.tercih ?? null;

  if (tercihHam === null || tercih === null) {
    return (
      <Screen title={M.donemsel.baslik}>
        <p className="p-4">
          <Spinner label={M.donemsel.tercihlerOkunuyor} />
        </p>
      </Screen>
    );
  }

  return (
    <Screen
      title={M.donemsel.baslik}
      description={M.donemsel.aciklama}
    >
      <div className="space-y-4 p-4">
        {/* Hesaplardan doldur ÜSTTE (kullanıcı, 18.10.2026); bir hesap
            işaretlenince Varlıklar bölümü kapanır — seçim oradan yapılıyor. */}
        {kural.mod !== "sinirsiz" && (
          <KilitliKabiliyet
            ne={
              kural.mod === "ucretsiz"
                ? M.donemsel.kilit.ucretsiz(kural.enCok)
                : M.donemsel.kilit.kendi
            }
            {...(etkin?.kisildi === true ? { aciklama: M.donemsel.kilit.kisildi } : {})}
          />
        )}
        <HesaplardanDoldur
          tercih={tercih}
          kural={kural}
          degistir={degistir}
          liste={listeKiyasli}
          kunye={kunye}
          onHesapSecildi={() => degistir({ listeAcik: false })}
        />
        <VarlikListesi
          merkezYok={merkez.central === null ? (merkez.unavailable ?? M.donemsel.sunucuyaBagliDegil) : null}
          liste={listeKiyasli}
          onYeniden={() => setListeYeniden((x) => x + 1)}
          secili={tercih.secili}
          kural={kural}
          acik={tercih.listeAcik}
          onAcik={(listeAcik) => degistir({ listeAcik })}
          onSecili={(secili) => degistir({ secili })}
          kunye={kunye}
        />
        <Ayarlar tercih={tercih} degistir={degistir} bugun={bugun} kunye={kunye} kural={kural} enEski={enEski} />
        <Analiz tercih={tercih} kunye={kunye} revision={revision} />
      </div>
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Varlık listesi
// ---------------------------------------------------------------------------

function aracAdi(id: string, kunye: ReadonlyMap<string, TrackedListItemDto>): string {
  const k = kunye.get(id);
  return k === undefined ? id : k.symbol;
}

function VarlikListesi({
  merkezYok,
  liste,
  onYeniden,
  secili,
  kural,
  acik,
  onAcik,
  onSecili,
  kunye,
}: {
  readonly merkezYok: string | null;
  readonly liste:
    | { readonly durum: "yukleniyor" }
    | { readonly durum: "tamam"; readonly items: readonly TrackedListItemDto[] }
    | { readonly durum: "hata"; readonly hata: unknown };
  readonly onYeniden: () => void;
  readonly secili: readonly string[];
  readonly kural: DonemselKural;
  readonly acik: boolean;
  readonly onAcik: (acik: boolean) => void;
  readonly onSecili: (ids: readonly string[]) => void;
  readonly kunye: ReadonlyMap<string, TrackedListItemDto>;
}) {
  const bolgeId = useId();
  const [ara, setAra] = useState("");
  const dolu = secili.length >= kural.enCok;

  const gruplar = useMemo(() => {
    if (liste.durum !== "tamam") return [];
    const igne = ara.trim().toLocaleLowerCase("tr");
    const m = new Map<string, TrackedListItemDto[]>();
    for (const i of liste.items) {
      if (
        igne !== "" &&
        ![i.symbol, i.name, i.instrumentId].some((f) => f.toLocaleLowerCase("tr").includes(igne))
      ) {
        continue;
      }
      // Kıyas serileri (BIST100, altın, döviz) kendi grubunda, EN ÜSTTE.
      const grup = i.kiyas === true ? KIYAS_GRUBU : i.assetClass;
      const g = m.get(grup) ?? [];
      g.push(i);
      m.set(grup, g);
    }
    return [...m.entries()]
      .map(([sinif, items]) => ({ sinif, ad: sinif === KIYAS_GRUBU ? M.donemsel.liste.kiyasGrubu : assetClassLabel(sinif), items }))
      .sort((a, b) =>
        a.sinif === KIYAS_GRUBU ? -1 : b.sinif === KIYAS_GRUBU ? 1 : a.ad.localeCompare(b.ad, "tr"),
      );
  }, [liste, ara]);

  const degistirSecim = (id: string, sec: boolean) => {
    if (sec) {
      if (secili.includes(id) || dolu) return;
      onSecili([...secili, id]);
    } else onSecili(secili.filter((x) => x !== id));
  };

  return (
    <section className="pf-card" aria-label={M.donemsel.liste.baslik} data-kart-bolum="liste">
      <button
        type="button"
        aria-expanded={acik}
        aria-controls={bolgeId}
        onClick={() => onAcik(!acik)}
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
      >
        <span className="text-[13px] font-semibold text-ink">
          {M.donemsel.liste.baslik}{" "}
          <span className="font-normal text-muted">{M.donemsel.liste.seciliSayisi(secili.length, kural.enCok)}</span>
        </span>
        <span aria-hidden="true" className="text-[12px] text-muted">
          {acik ? M.donemsel.liste.gizle : M.donemsel.liste.goster}
        </span>
      </button>
      {!acik && secili.length > 0 && (
        <p className="border-t border-line px-3 py-2 text-[12px] text-muted">
          {secili.map((id) => aracAdi(id, kunye)).join(" · ")}
        </p>
      )}
      <div id={bolgeId} hidden={!acik} className="border-t border-line p-3">
        {merkezYok !== null ? (
          <Notice tone="warn" title={M.donemsel.liste.okunamiyor}>
            {merkezYok}
          </Notice>
        ) : liste.durum === "yukleniyor" ? (
          <Spinner label={M.donemsel.liste.yukleniyor} />
        ) : liste.durum === "hata" ? (
          <ErrorBlock title={M.donemsel.liste.okunamadi} error={liste.hata} onRetry={onYeniden} />
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <Field label={M.donemsel.liste.ara} htmlFor="donemsel-ara" className="min-w-0 flex-1 sm:max-w-xs">
                <TextInput
                  id="donemsel-ara"
                  type="search"
                  value={ara}
                  placeholder={M.donemsel.liste.araIpucu}
                  onChange={(e) => setAra(e.currentTarget.value)}
                />
              </Field>
              {secili.length > 0 && (
                <Button onClick={() => onSecili([])}>{M.donemsel.liste.secimiTemizle}</Button>
              )}
            </div>
            {dolu && (
              <p className="mt-2 text-[12px] text-muted">
                {M.donemsel.liste.dolu(kural.enCok)}
              </p>
            )}
            {gruplar.length === 0 ? (
              <p className="mt-3 text-[13px] text-muted">
                {liste.items.length === 0 ? M.donemsel.liste.bos : M.donemsel.liste.uyanYok}
              </p>
            ) : (
              // Kaydırma yok, yalnızca sembol; ad balonda ve ekran okuyucuda
              // (kullanıcı, 14.10.2026). Arama adda da arar.
              <div className="mt-3 space-y-3">
                {gruplar.map((g) => (
                  <fieldset key={g.sinif}>
                    <legend className="mb-1 text-[11px] font-semibold tracking-wide text-muted uppercase">
                      {g.ad}
                    </legend>
                    <ul className="grid grid-cols-3 gap-x-3 gap-y-1 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10">
                      {g.items.map((i) => {
                        const hazir = HAZIR_DURUMLAR.has(i.state);
                        const isaretli = secili.includes(i.instrumentId);
                        return (
                          <li key={i.instrumentId}>
                            <label
                              title={hazir ? i.name : M.donemsel.liste.hazirlaniyorBalon(i.name)}
                              className={cx(
                                "flex min-w-0 items-baseline gap-1.5 text-[13px]",
                                hazir ? "cursor-pointer text-ink" : "text-faint",
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={isaretli}
                                disabled={
                                  !hazir || (!isaretli && (dolu || !secilebilir(kural, i.instrumentId, i.kiyas === true)))
                                }
                                onChange={(e) => degistirSecim(i.instrumentId, e.currentTarget.checked)}
                              />
                              <span className="min-w-0 truncate font-medium">{i.symbol}</span>
                              <span className="sr-only">
                                {" "}
                                {i.name}
                                {!hazir && M.donemsel.liste.hazirlaniyor}
                              </span>
                              {!hazir && (
                                <span aria-hidden="true" className="shrink-0 text-[11px]">
                                  …
                                </span>
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </fieldset>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Hesaplardan doldur (kullanıcı, 15.10.2026)
// ---------------------------------------------------------------------------

interface HesapSecenegi {
  readonly id: string;
  readonly ad: string;
  readonly renk: Parameters<typeof HesapAdi>[0]["renk"];
  readonly sanal: boolean;
}

/**
 * Seçilen gerçek ve sanal hesaplardaki varlıklar işaretlenir, sepet açılır
 * ve oranlar bugünkü değerlerinden yazılır. Oranları MOTOR hesaplar
 * (`sepetDagilimi`); burada yalnızca sonuç tercihe yazılır. Seçili kıyas
 * serileri yer kaldıkça korunur — sepete girmezler, kıyas için dururlar.
 */
function HesaplardanDoldur({
  tercih,
  kural,
  degistir,
  liste,
  kunye,
  onHesapSecildi,
}: {
  readonly tercih: DonemselTercih;
  readonly kural: DonemselKural;
  readonly degistir: (yeni: Partial<DonemselTercih>) => void;
  readonly liste:
    | { readonly durum: "yukleniyor" }
    | { readonly durum: "tamam"; readonly items: readonly TrackedListItemDto[] }
    | { readonly durum: "hata"; readonly hata: unknown };
  readonly kunye: ReadonlyMap<string, TrackedListItemDto>;
  /** Bir hesap işaretlenince (kaldırılınca değil). */
  readonly onHesapSecildi: () => void;
}) {
  const { repo, revision } = useRepositoryContext();
  const [hesaplar, setHesaplar] = useState<readonly HesapSecenegi[] | null>(null);
  const [secim, setSecim] = useState<ReadonlySet<string>>(new Set());
  const [durum, setDurum] = useState<
    | { readonly tur: "bos" }
    | { readonly tur: "calisiyor" }
    | { readonly tur: "tamam"; readonly sonuc: SepetDagilimi }
    | { readonly tur: "hata"; readonly hata: unknown }
  >({ tur: "bos" });

  useEffect(() => {
    let iptal = false;
    void (async () => {
      const [gercek, sanal] = await Promise.all([
        repo.accounts.list(),
        repo.sanalHesaplar?.liste().catch(() => []) ?? Promise.resolve([]),
      ]);
      if (iptal) return;
      setHesaplar([
        ...gercek.map((a) => ({ id: a.id, ad: a.name, renk: a.renk, sanal: false })),
        ...sanal.map((h) => ({ id: h.id, ad: h.ad, renk: h.renk, sanal: true })),
      ]);
    })().catch(() => {
      if (!iptal) setHesaplar([]);
    });
    return () => {
      iptal = true;
    };
  }, [repo, revision]);

  if (hesaplar === null || hesaplar.length === 0 || repo.sepetDagilimi === undefined) return null;

  const izinli =
    liste.durum === "tamam"
      ? liste.items.filter((i) => HAZIR_DURUMLAR.has(i.state) && i.kiyas !== true).map((i) => i.instrumentId)
      : [];

  const doldur = async () => {
    if (repo.sepetDagilimi === undefined) return;
    setDurum({ tur: "calisiyor" });
    try {
      const sonuc = await repo.sepetDagilimi({
        accountIds: [...secim],
        reportCurrency: tercih.para,
        izinli,
      });
      if (sonuc.agirliklar.length > 0) {
        const uyeler = sonuc.agirliklar.map((a) => a.instrumentId);
        const kiyaslar = tercih.secili.filter((id) => kiyasMi(id, kunye));
        // Gelir modeli: ücretsizde değere göre en büyük 3'ü işaretli gelir; sepet
        // yalnızca açık kipte kendiliğinden açılır.
        const secili = [...uyeler, ...kiyaslar].slice(0, kural.enCok);
        degistir({
          secili,
          sepetAcik: kural.sepetAcik && uyeler.length >= 2,
          ...(uyeler.length >= 2 ? {} : { yalnizSepet: false }),
          agirliklar: {
            ...tercih.agirliklar,
            ...Object.fromEntries(sonuc.agirliklar.map((a) => [a.instrumentId, kesirdenYuzde(a.agirlik) ?? ""])),
          },
        });
      }
      setDurum({ tur: "tamam", sonuc });
    } catch (hata) {
      setDurum({ tur: "hata", hata });
    }
  };

  return (
    <section className="pf-card p-3" aria-label={M.donemsel.doldur.baslik} data-kart-bolum="form">
      <p className="text-[13px] font-semibold text-ink">
        {M.donemsel.doldur.baslik}{" "}
        <span className="font-normal text-muted">{M.donemsel.doldur.altBaslik}</span>
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {hesaplar.map((h) => (
          <label key={h.id} className="inline-flex cursor-pointer items-center gap-1.5 text-[13px]">
            <input
              type="checkbox"
              checked={secim.has(h.id)}
              onChange={(e) => {
                const yeni = new Set(secim);
                if (e.currentTarget.checked) {
                  yeni.add(h.id);
                  onHesapSecildi();
                } else yeni.delete(h.id);
                setSecim(yeni);
              }}
            />
            <HesapAdi renk={h.renk}>{h.ad}</HesapAdi>
            {h.sanal && <span className="text-[11px] text-faint">{M.donemsel.doldur.sanal}</span>}
          </label>
        ))}
        <Button
          disabled={secim.size === 0 || durum.tur === "calisiyor" || liste.durum !== "tamam"}
          onClick={() => void doldur()}
        >
          {durum.tur === "calisiyor" ? M.donemsel.doldur.hesaplaniyor : M.donemsel.doldur.secVeOranla}
        </Button>
      </div>
      {durum.tur === "hata" && (
        <div className="mt-2">
          <ErrorBlock title={M.donemsel.doldur.hata} error={durum.hata} onRetry={() => void doldur()} />
        </div>
      )}
      {durum.tur === "tamam" && (
        <div className="mt-2 text-[12px] text-muted" role="status">
          {durum.sonuc.agirliklar.length === 0 ? (
            <p className="text-warn">{M.donemsel.doldur.varlikYok}</p>
          ) : (
            <p>
              {durum.sonuc.agirliklar.length >= 2
                ? M.donemsel.doldur.secildiSepetli(durum.sonuc.agirliklar.length)
                : M.donemsel.doldur.secildiTek(durum.sonuc.agirliklar.length)}
            </p>
          )}
          {durum.sonuc.disarida.length > 0 && (
            <p>
              {M.donemsel.doldur.disarida}{" "}
              {durum.sonuc.disarida.map((d, i) => (
                <span key={`${d.ad}|${i}`}>
                  {i > 0 && " · "}
                  <span className="text-ink">{d.ad}</span> ({motorMetni(d.sebep)})
                </span>
              ))}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Ayarlar: dönem, para birimi, periyot, temettü, sepet
// ---------------------------------------------------------------------------

function Ayarlar({
  tercih,
  degistir,
  bugun,
  kunye,
  kural,
  enEski,
}: {
  readonly tercih: DonemselTercih;
  readonly degistir: (yeni: Partial<DonemselTercih>) => void;
  readonly bugun: IsoDate;
  readonly kunye: ReadonlyMap<string, TrackedListItemDto>;
  readonly kural: DonemselKural;
  /** Fiyat geçmişi derinliğinin en eski günü (gelir modeli). */
  readonly enEski: IsoDate | undefined;
}) {
  const hazirPeriyot = PERIYOTLAR.some((p) => p.gun === tercih.periyotGun);
  const [ozel, setOzel] = useState(!hazirPeriyot);
  const [ozelMetin, setOzelMetin] = useState(String(tercih.periyotGun));
  const ozelGecerli = /^\d{1,4}$/.test(ozelMetin) && Number(ozelMetin) >= 1 && Number(ozelMetin) <= DONEMSEL_MAX_PERIYOT_GUN;

  const uyeler = sepetUyeleri(tercih.secili, kunye);
  const sepetAc = (acik: boolean) => {
    if (acik && uyeler.every((id) => (tercih.agirliklar[id] ?? "") === "")) {
      const esit = esitDagit(uyeler.length);
      degistir({
        sepetAcik: true,
        agirliklar: Object.fromEntries(uyeler.map((id, i) => [id, esit[i] ?? ""])),
      });
      return;
    }
    degistir({ sepetAcik: acik, ...(acik ? {} : { yalnizSepet: false }) });
  };

  return (
    <section className="pf-card p-3" aria-label={M.donemsel.ayarlar.bolge} data-kart-bolum="form">
      <div className="grid grid-cols-2 items-end gap-2 sm:flex sm:flex-wrap">
        <TarihAraligiSecici
          idOnek="donemsel"
          from={tercih.from}
          to={tercih.to}
          max={bugun}
          {...(enEski === undefined ? {} : { min: enEski })}
          bugun={bugun}
          donem={tercih.donem}
          onChange={(a, donem: DonemKodu | null) => degistir({ from: a.from, to: a.to, donem })}
          alanSinifi="min-w-0 sm:w-40"
          donemSinifi="col-span-2 sm:col-span-1"
        />
        <Field label={M.donemsel.ayarlar.paraBirimi} htmlFor="donemsel-para" className="col-span-2 min-w-0 sm:col-span-1 sm:w-56">
          <CurrencySelect id="donemsel-para" value={tercih.para} onChange={(para) => degistir({ para })} />
        </Field>
        <Field label={M.donemsel.ayarlar.periyot} htmlFor="donemsel-periyot" className="col-span-2 min-w-0 sm:col-span-1 sm:w-48">
          <Select
            id="donemsel-periyot"
            value={ozel ? "ozel" : String(tercih.periyotGun)}
            options={[
              ...PERIYOTLAR.map((p) => ({ value: String(p.gun), label: p.ad })),
              { value: "ozel", label: M.donemsel.ayarlar.ozelPeriyot },
            ]}
            onChange={(e) => {
              const v = e.currentTarget.value;
              if (v === "ozel") {
                setOzel(true);
                setOzelMetin(String(tercih.periyotGun));
                return;
              }
              setOzel(false);
              degistir({ periyotGun: Number(v) });
            }}
          />
        </Field>
        {ozel && (
          <Field
            label={M.donemsel.ayarlar.gun}
            htmlFor="donemsel-gun"
            className="col-span-2 min-w-0 sm:col-span-1 sm:w-28"
            {...(ozelGecerli ? {} : { error: M.donemsel.ayarlar.tamSayiAraligi(1, DONEMSEL_MAX_PERIYOT_GUN) })}
          >
            <TextInput
              id="donemsel-gun"
              numeric
              inputMode="numeric"
              value={ozelMetin}
              onChange={(e) => {
                const m = e.currentTarget.value;
                setOzelMetin(m);
                if (/^\d{1,4}$/.test(m) && Number(m) >= 1 && Number(m) <= DONEMSEL_MAX_PERIYOT_GUN) {
                  degistir({ periyotGun: Number(m) });
                }
              }}
            />
          </Field>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-ink">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={tercih.temettu} onChange={(e) => degistir({ temettu: e.currentTarget.checked })} />
          {M.donemsel.ayarlar.temettu}
        </label>
        <OrtalamaSecimi tercih={tercih} degistir={degistir} />
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={tercih.sepetAcik}
            disabled={!kural.sepetAcik}
            onChange={(e) => sepetAc(e.currentTarget.checked)}
          />
          {M.donemsel.ayarlar.sepetCiz}
          {!kural.sepetAcik && <KilitliKabiliyet ne={M.donemsel.kilit.sepet} kisa="" className="ml-1" />}
        </label>
        {tercih.sepetAcik && (
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={tercih.yalnizSepet}
              onChange={(e) => degistir({ yalnizSepet: e.currentTarget.checked })}
            />
            {M.donemsel.ayarlar.yalnizSepet}
          </label>
        )}
      </div>
      {tercih.sepetAcik && <SepetAyarlari tercih={tercih} degistir={degistir} kunye={kunye} />}
    </section>
  );
}

/**
 * "Ortalama fiyat: son N gün" — her günün fiyatı son N işlem gününün
 * ortalaması (kullanıcı kararı 13.10.2026; varsayılan açık, 7 gün).
 */
function OrtalamaSecimi({
  tercih,
  degistir,
}: {
  readonly tercih: DonemselTercih;
  readonly degistir: (yeni: Partial<DonemselTercih>) => void;
}) {
  const [metin, setMetin] = useState(String(tercih.ortalamaGun));
  const gecerli = (m: string) =>
    /^\d{1,2}$/.test(m) && Number(m) >= DONEMSEL_MIN_ORTALAMA_GUN && Number(m) <= DONEMSEL_MAX_ORTALAMA_GUN;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <input
        id="donemsel-ortalama"
        type="checkbox"
        checked={tercih.ortalamaAcik}
        onChange={(e) => degistir({ ortalamaAcik: e.currentTarget.checked })}
      />
      <label htmlFor="donemsel-ortalama">{M.donemsel.ayarlar.ortalamaEtiket}</label>
      <TextInput
        aria-label={M.donemsel.ayarlar.ortalamaGirdi}
        numeric
        inputMode="numeric"
        className="w-14"
        disabled={!tercih.ortalamaAcik}
        aria-invalid={!gecerli(metin) || undefined}
        value={metin}
        onChange={(e) => {
          const m = e.currentTarget.value;
          setMetin(m);
          if (gecerli(m)) degistir({ ortalamaGun: Number(m) });
        }}
      />
      <span>{M.donemsel.ayarlar.islemGunu}</span>
      {!gecerli(metin) && (
        <span className="text-[12px] text-warn">
          {M.donemsel.ayarlar.tamSayiAraligi(DONEMSEL_MIN_ORTALAMA_GUN, DONEMSEL_MAX_ORTALAMA_GUN)}
        </span>
      )}
    </span>
  );
}

function SepetAyarlari({
  tercih,
  degistir,
  kunye,
}: {
  readonly tercih: DonemselTercih;
  readonly degistir: (yeni: Partial<DonemselTercih>) => void;
  readonly kunye: ReadonlyMap<string, TrackedListItemDto>;
}) {
  const uyeler = sepetUyeleri(tercih.secili, kunye);
  const disarida = tercih.secili.length - uyeler.length;
  const metinler = uyeler.map((id) => tercih.agirliklar[id] ?? "");
  const t = toplam(metinler);
  const yaz = (id: string, metin: string) => degistir({ agirliklar: { ...tercih.agirliklar, [id]: metin } });
  const hepsiniYaz = (yeni: readonly string[]) =>
    degistir({ agirliklar: { ...tercih.agirliklar, ...Object.fromEntries(uyeler.map((id, i) => [id, yeni[i] ?? ""])) } });

  if (uyeler.length < 2) {
    return (
      <p className="mt-3 text-[13px] text-muted">
        {M.donemsel.sepet.enAzIki}
      </p>
    );
  }
  return (
    <div className="mt-3 border-t border-line pt-3">
      <div className="flex flex-wrap items-end gap-2">
        <Field label={M.donemsel.sepet.dengeleme} htmlFor="donemsel-dengeleme" className="min-w-0 sm:w-48">
          <Select
            id="donemsel-dengeleme"
            value={tercih.dengeleme}
            options={DENGELEMELER.map((d) => ({ value: d.value, label: d.label }))}
            onChange={(e) => degistir({ dengeleme: e.currentTarget.value as SepetDengeleme })}
          />
        </Field>
        <Button onClick={() => hepsiniYaz(esitDagit(uyeler.length))}>{M.donemsel.sepet.esitDagit}</Button>
      </div>
      {disarida > 0 && (
        <p className="mt-2 text-[12px] text-muted">{M.donemsel.sepet.kiyasGirmez}</p>
      )}
      {/* Kutu adın HEMEN yanında (kullanıcı, 14.10.2026): ad ile kutu
          arasında boşluk kalmasın. */}
      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
        {uyeler.map((id, i) => {
          const m = metinler[i] ?? "";
          const gecersiz = m !== "" && yuzdeOku(m) === null;
          return (
            <li key={id} className="flex items-center gap-1.5">
              <label htmlFor={`donemsel-agirlik-${i}`} className="text-[13px] font-medium text-ink">
                {aracAdi(id, kunye)}
              </label>
              <span className="text-[13px] text-muted">%</span>
              <TextInput
                id={`donemsel-agirlik-${i}`}
                numeric
                inputMode="decimal"
                aria-invalid={gecersiz || undefined}
                className="w-20"
                value={m}
                onChange={(e) => yaz(id, e.currentTarget.value)}
              />
            </li>
          );
        })}
      </ul>
      <p className="mt-2 flex flex-wrap items-center gap-2 text-[13px]">
        {t === null ? (
          <span className="text-warn">{M.donemsel.sepet.gecersiz}</span>
        ) : t === YUZ ? (
          <span className="text-muted">{M.donemsel.sepet.toplamTam}</span>
        ) : (
          <>
            <span className="text-warn">{M.donemsel.sepet.toplamEksik(yuzdeYaz(t))}</span>
            {t > 0 && (
              <Button
                onClick={() => {
                  const yeni = yuzeOlcekle(metinler);
                  if (yeni !== null) hepsiniYaz(yeni);
                }}
              >
                {M.donemsel.sepet.olcekle}
              </Button>
            )}
          </>
        )}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Analiz: eşitle → hesapla → çiz
// ---------------------------------------------------------------------------

/** İsteğin kurulamama sebebi ya da istek. */
function istekKur(
  tercih: DonemselTercih,
  kunye: ReadonlyMap<string, TrackedListItemDto> = new Map(),
): { readonly istek: DonemselAnalizIstegi } | { readonly sebep: string; readonly sepetSebebi?: true } {
  if (tercih.secili.length === 0) return { sebep: M.donemsel.istek.varlikSec };
  if (tercih.from === "" || tercih.to === "") return { sebep: M.donemsel.istek.ikiTarih };
  if (tercih.from > tercih.to) return { sebep: M.donemsel.istek.sira };
  if (gunFarki(tercih.from, tercih.to) + 1 > DONEMSEL_MAX_GUN) {
    return { sebep: M.donemsel.istek.enCokGun(DONEMSEL_MAX_GUN) };
  }
  if (!isKnownCurrency(tercih.para)) return { sebep: M.donemsel.istek.paraSec };
  let sepet: DonemselAnalizIstegi["sepet"] = null;
  const uyeler = sepetUyeleri(tercih.secili, kunye);
  if (tercih.sepetAcik && uyeler.length >= 2) {
    const metinler = uyeler.map((id) => tercih.agirliklar[id] ?? "");
    const t = toplam(metinler);
    if (t !== YUZ) {
      return { sebep: M.donemsel.istek.sepetToplam, sepetSebebi: true };
    }
    sepet = {
      dengeleme: tercih.dengeleme,
      agirliklar: uyeler.map((id, i) => ({
        instrumentId: id,
        agirlik: kesirMetni(yuzdeOku(metinler[i] ?? "") ?? 0),
      })),
    };
  }
  return {
    istek: {
      instrumentIds: tercih.secili,
      from: tercih.from,
      to: tercih.to,
      reportCurrency: tercih.para,
      periyotGun: tercih.periyotGun,
      temettuYenidenYatir: tercih.temettu,
      ortalamaGun: tercih.ortalamaAcik ? tercih.ortalamaGun : null,
      sepet,
    },
  };
}

/** Merkezin kur kaynağı yalnızca X/TRY yayımlıyor (`esitlemeHedefi.ts`). */
function kurCiftleri(paralar: readonly (string | null)[]): (readonly [CurrencyCode, CurrencyCode])[] {
  const k = new Set<string>();
  for (const p of paralar) if (p !== null && p !== "TRY" && isKnownCurrency(p)) k.add(p);
  return [...k].sort().map((c) => [c, "TRY"] as const);
}

function Analiz({
  tercih,
  kunye,
  revision,
}: {
  readonly tercih: DonemselTercih;
  readonly kunye: ReadonlyMap<string, TrackedListItemDto>;
  readonly revision: number;
}) {
  const { repo } = useRepositoryContext();
  const merkez = useCentral();
  const kurulum = useMemo(() => istekKur(tercih, kunye), [tercih, kunye]);
  const istek = "istek" in kurulum ? kurulum.istek : null;
  const anahtar = istek === null ? "" : JSON.stringify(istek);
  const [sonuc, setSonuc] = useState<Sonuc | null>(null);
  const [durum, setDurum] = useState<Durum>({ tur: "bos" });
  const [yeniden, setYeniden] = useState(0);
  // Aynı veri ihtiyacı (araçlar + aralık + para) bir oturumda bir kez eşitlenir.
  const esitlenen = useRef(new Set<string>());
  const baglam = useRef({ repo, merkez, kunye });
  baglam.current = { repo, merkez, kunye };

  useEffect(() => {
    if (istek === null) {
      setDurum({ tur: "bos" });
      return undefined;
    }
    let iptal = false;
    const zamanlayici = setTimeout(() => {
      void (async () => {
        const { repo: r, merkez: m, kunye: k } = baglam.current;
        const uyarilar: string[] = [];
        try {
          // Periyot + ortalama fiyat için geriye bakış (motorla aynı sayı).
          const ortalamaEk = istek.ortalamaGun == null ? 0 : ortalamaGeriGun(istek.ortalamaGun);
          const geriBas = gunEkle(istek.from, -(istek.periyotGun + ortalamaEk));
          const veriAnahtari = `${[...istek.instrumentIds].sort().join(",")}|${geriBas}|${istek.to}|${istek.reportCurrency}`;
          if (m.central !== null && m.prices !== null && m.sync !== null && !esitlenen.current.has(veriAnahtari)) {
            setDurum({ tur: "calisiyor", adim: "esitleniyor" });
            let fiyat: SyncReport | null = null;
            let kur: FxSyncResult | null = null;
            let kirildi = false;
            try {
              // Döviz kıyasları araç değil: fiyatı yok, yalnızca kuru eşitlenir.
              const araclar = istek.instrumentIds.filter((id) => !id.startsWith(KUR_KIYAS_ONEKI));
              fiyat = araclar.length === 0 ? null : await m.sync.syncPrices({
                cache: m.prices,
                central: m.central,
                instrumentIds: araclar,
                from: geriBas,
                to: istek.to,
                ensureTracked: true,
              });
              uyarilar.push(...(fiyat?.errors ?? []));
            } catch (err) {
              kirildi = true;
              uyarilar.push(M.donemsel.analiz.uyariFiyat(mesaj(err)));
            }
            const paralar = istek.instrumentIds.map((id) => k.get(id)?.currency ?? null);
            const ciftler = kurCiftleri([...paralar, istek.reportCurrency]);
            if (ciftler.length > 0) {
              try {
                kur = await m.sync.syncFx({ cache: m.prices, central: m.central, pairs: ciftler, from: geriBas, to: istek.to });
                uyarilar.push(...kur.errors);
              } catch (err) {
                kirildi = true;
                uyarilar.push(M.donemsel.analiz.uyariKur(mesaj(err)));
              }
            }
            // Portföy grafiğinin önbelleği: değişen günden sonrası atılır
            // (aynı kural otomatik eşitlemede de — `EsitlemeSaglayici`).
            const degisim = esitlemeninDegisimi(fiyat, kur, kirildi);
            fiyatlarDegisti(degisim);
            if (degisim !== null) await r.seriOnbellegi?.sil(degisim).catch(() => undefined);
            if (!kirildi && uyarilar.length === 0) esitlenen.current.add(veriAnahtari);
          }
          if (iptal) return;
          setDurum({ tur: "calisiyor", adim: "hesaplaniyor" });
          if (r.donemselAnaliz === undefined) throw new Error(M.donemsel.analiz.derlemedeYok);
          const veri = await r.donemselAnaliz(istek);
          if (iptal) return;
          setSonuc({ anahtar, veri, uyarilar });
          setDurum({ tur: "bos" });
        } catch (hata) {
          if (!iptal) setDurum({ tur: "hata", hata });
        }
      })();
    }, 350);
    return () => {
      iptal = true;
      clearTimeout(zamanlayici);
    };
    // `istek` anahtarla temsil ediliyor.
  }, [anahtar, revision, yeniden]);

  const gecerli = sonuc !== null && sonuc.anahtar === anahtar ? sonuc : null;
  const calisiyor = durum.tur === "calisiyor";

  if ("sebep" in kurulum) {
    return kurulum.sepetSebebi === true ? (
      <Notice tone="warn" title={M.donemsel.analiz.sepet}>
        {kurulum.sebep}
      </Notice>
    ) : (
      <EmptyState title={M.donemsel.analiz.secimBekleniyor} description={kurulum.sebep} />
    );
  }

  return (
    <div className="space-y-4">
      {durum.tur === "hata" && <ErrorBlock title={M.donemsel.analiz.hesaplanamadi} error={durum.hata} onRetry={() => setYeniden((x) => x + 1)} />}
      {calisiyor && gecerli === null && (
        <div role="status" aria-live="polite" className="pf-card flex flex-col items-center justify-center gap-3 py-16 text-center">
          <span
            aria-hidden="true"
            className="inline-block size-8 animate-spin rounded-full border-[3px] border-line-strong border-t-accent"
          />
          <span className="text-[13px] text-muted">
            {durum.adim === "esitleniyor" ? M.donemsel.analiz.esitleniyor : M.donemsel.analiz.hesaplaniyor}
          </span>
        </div>
      )}
      {calisiyor && gecerli !== null && (
        <p className="flex justify-end">
          <Spinner label={M.donemsel.analiz.guncelleniyor} />
        </p>
      )}
      {gecerli !== null && (
        <>
          {gecerli.uyarilar.length > 0 && (
            <Notice tone="warn" title={M.donemsel.analiz.kismiBaslik}>
              {M.donemsel.analiz.kismiMetin(gecerli.uyarilar.slice(0, 3).join(" · "))}
            </Notice>
          )}
          <Sonuclar veri={gecerli.veri} kunye={kunye} yalnizSepet={tercih.sepetAcik && tercih.yalnizSepet} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sonuçlar: grafik + seçili gün + özet tablo
// ---------------------------------------------------------------------------

interface Cizgi {
  readonly kimlik: string;
  readonly ad: string;
  readonly uzunAd: string;
  readonly renk: string;
  readonly kesikli: boolean;
  readonly seri: DonemselSeri;
}

function Sonuclar({
  veri,
  kunye,
  yalnizSepet,
}: {
  readonly veri: DonemselAnalizSonucu;
  readonly kunye: ReadonlyMap<string, TrackedListItemDto>;
  readonly yalnizSepet: boolean;
}) {
  const cizgiler = useMemo(() => {
    const out: Cizgi[] = [];
    // "Yalnızca sepet": tekil varlıklar gizlenir, KIYAS serileri kalır —
    // sepet onlarla kıyaslanmak için çizilir.
    const herkes = !yalnizSepet || veri.sepet === null;
    veri.seriler.forEach((s, i) => {
      if (!herkes && !kiyasMi(s.kimlik, kunye)) return;
      const r = CIZGI_RENKLERI[i % CIZGI_RENKLERI.length] ?? { renk: SEPET_RENGI, kesikli: false };
      const k = kunye.get(s.kimlik);
      out.push({
        kimlik: s.kimlik,
        ad: k?.symbol ?? s.kimlik,
        uzunAd: k === undefined ? s.kimlik : `${k.symbol} — ${k.name}`,
        renk: r.renk,
        kesikli: r.kesikli,
        seri: s,
      });
    });
    if (veri.sepet !== null) {
      out.push({ kimlik: "sepet", ad: M.donemsel.sonuc.sepet, uzunAd: M.donemsel.sonuc.sepet, renk: SEPET_RENGI, kesikli: false, seri: veri.sepet });
    }
    return out;
  }, [veri, kunye, yalnizSepet]);

  const gunler = useMemo(() => {
    const out: IsoDate[] = [];
    for (let d = veri.from; d <= veri.to; d = gunEkle(d, 1)) out.push(d);
    return out;
  }, [veri.from, veri.to]);

  const [kapali, setKapali] = useState<ReadonlySet<string>>(new Set());
  const yakin = useYakinlastirma(gunler.length);
  const [secili, setSecili] = useState<number | null>(null);
  useEffect(() => setSecili(null), [veri]);
  const seciliIndeks = secili !== null && secili < gunler.length ? secili : gunler.length - 1;

  if (gunler.length === 0) {
    return (
      <EmptyState
        title={M.donemsel.sonuc.gunYokBaslik}
        description={M.donemsel.sonuc.gunYokAciklama}
      />
    );
  }

  const baslik =
    M.donemsel.sonuc.baslik(veri.periyotGun, veri.reportCurrency) +
    (veri.ortalamaGun === null ? "" : M.donemsel.sonuc.baslikOrtalama(veri.ortalamaGun)) +
    (veri.temettuYenidenYatir ? M.donemsel.sonuc.baslikTemettu : "") +
    M.donemsel.sonuc.baslikSon;
  return (
    <>
      {/* ÖZET ÖNCE (kullanıcı, 18.10.2026): dönem içi dağılım tablosu
          sonuçların başında, grafikten önce. */}
      <OzetTablosu cizgiler={cizgiler} periyot={veri.periyotGun} />
      {veri.from !== veri.istenenFrom && (
        <Notice tone="info" title={M.donemsel.sonuc.gecBaslik}>
          {M.donemsel.sonuc.gecBaslikMetin(formatIsoDate(veri.from))}
        </Notice>
      )}
      <SicramaUyarisi cizgiler={cizgiler} from={gunEkle(veri.from, -veri.periyotGun)} to={veri.to} />
      <AnalizGrafigi
        baslik={baslik}
        cizgiler={cizgiler}
        gunler={gunler}
        yakin={yakin}
        kapali={kapali}
        onDegistir={(k) =>
          setKapali((s) => {
            const y = new Set(s);
            if (y.has(k)) y.delete(k);
            else y.add(k);
            return y;
          })
        }
        secili={seciliIndeks}
        onSec={setSecili}
        periyot={veri.periyotGun}
        para={veri.reportCurrency}
      />
      <SeciliGun
        gun={gunler[seciliIndeks]}
        indeks={seciliIndeks}
        cizgiler={cizgiler}
        periyot={veri.periyotGun}
        para={veri.reportCurrency}
      />
    </>
  );
}

/**
 * Olağan dışı fiyat sıçramaları (motorun `sicramalar`ı): grafiğe etki eden
 * aralıkta olanlar. Ekranda görünen her işaret bir anormallik: kaynak bir
 * bedelsizi/bölünmeyi işlememiş olabilir (kullanıcı kararı 13.10.2026).
 */
function SicramaUyarisi({
  cizgiler,
  from,
  to,
}: {
  readonly cizgiler: readonly Cizgi[];
  readonly from: IsoDate;
  readonly to: IsoDate;
}) {
  const satirlar = cizgiler.flatMap((c) =>
    c.seri.sicramalar.filter((x) => x.date > from && x.date <= to).map((x) => ({ c, x })),
  );
  if (satirlar.length === 0) return null;
  return (
    <Notice tone="warn" title={M.donemsel.sicrama.baslik}>
      <ul className="space-y-1">
        {satirlar.map(({ c, x }) => (
          <li key={`${c.kimlik}-${x.date}`}>
            <span className="font-medium">{c.ad}</span> · {formatIsoDate(x.date)}: {M.donemsel.sicrama.kapanis}{" "}
            <span className="pf-tek">{sayiYaz(x.oncekiFiyat)}</span> →{" "}
            <span className="pf-tek">
              {sayiYaz(x.fiyat)}
              {c.seri.paraBirimi === null ? "" : ` ${c.seri.paraBirimi}`}
            </span>{" "}
            (<span className="pf-tek">{formatRatio(x.degisim)}</span>
            {M.donemsel.sicrama.birIslemGunu}).{" "}
            {x.bilinenOlay === true
              ? M.donemsel.sicrama.olayDuzeltilmemis
              : x.bilinenOlay === false
                ? M.donemsel.sicrama.olayYok
                : M.donemsel.sicrama.olayBilinmiyor}{" "}
            {M.donemsel.sicrama.yaniltici}
          </li>
        ))}
      </ul>
    </Notice>
  );
}

function boslukSebebi(s: DonemselSeri, d: IsoDate): Absence | undefined {
  return s.bosluklar.find((b) => b.from <= d && d <= b.to)?.absence;
}

/** Değer metni: 1'in üstünde 2, altında en çok 6 ondalık (kripto, küçük fiyat). */
function sayiYaz(v: string): string {
  const mutlak = v.startsWith("-") ? v.slice(1) : v;
  return formatDecimal(v, Number(mutlak) >= 1 ? { fractionDigits: 2 } : { maxFractionDigits: 6 });
}

/**
 * Bir periyot ucunun değeri: rapor para biriminde değer; altında, gerekiyorsa,
 * aracın kendi para birimindeki kapanış, kapanışın gerçek günü ve temettüyle
 * biriken birim. Kullanıcı bir dış kaynakla karşılaştırıp denetleyebilsin
 * diye (kullanıcı, 13.10.2026).
 */
function UcDegeri({
  u,
  para,
  aracPara,
}: {
  readonly u: DonemselDeger | null;
  readonly para: string;
  readonly aracPara: string | null;
}) {
  if (u === null) return <span className="text-faint">—</span>;
  const ayrinti: string[] = [];
  if (u.ortalama !== undefined && u.fiyat !== undefined) {
    ayrinti.push(
      `${M.donemsel.uc.ortKapanis(`${sayiYaz(u.fiyat)}${aracPara === null ? "" : ` ${aracPara}`}`)} · ` +
        M.donemsel.uc.ortGunler(u.ortalama.gun, formatIsoDate(u.ortalama.from), formatIsoDate(u.ortalama.to)),
    );
  } else if (u.fiyat !== undefined && (aracPara !== para || u.birim !== undefined)) {
    ayrinti.push(M.donemsel.uc.kapanis(`${sayiYaz(u.fiyat)}${aracPara === null ? "" : ` ${aracPara}`}`));
  }
  if (u.fiyatTarihi !== undefined && u.fiyatTarihi !== u.date) {
    ayrinti.push(M.donemsel.uc.fiyatGunu(formatIsoDate(u.fiyatTarihi)));
  }
  if (u.birim !== undefined) ayrinti.push(M.donemsel.uc.birim(formatDecimal(u.birim, { maxFractionDigits: 6 })));
  return (
    <span className="block">
      <span className="pf-num text-ink">{sayiYaz(u.deger)}</span>
      {u.fiyat !== undefined && <span className="ml-1 text-[11px] text-faint">{para}</span>}
      {ayrinti.length > 0 && <span className="block text-[11px] text-faint">{ayrinti.join(" · ")}</span>}
    </span>
  );
}

function SeciliGun({
  gun,
  indeks,
  cizgiler,
  periyot,
  para,
}: {
  readonly gun: IsoDate | undefined;
  readonly indeks: number;
  readonly cizgiler: readonly Cizgi[];
  readonly periyot: number;
  readonly para: string;
}) {
  if (gun === undefined) return null;
  return (
    <section aria-label={M.donemsel.seciliGun.bolge} className="pf-card overflow-x-auto p-3" data-kart-bolum="ozet">
      <p className="text-[13px] text-ink">
        <span className="font-semibold">{M.donemsel.seciliGun.periyotSonu(formatIsoDate(gun))}</span>
        <span className="text-muted">
          {" "}
          {M.donemsel.seciliGun.periyotBasiAciklama(formatIsoDate(gunEkle(gun, -periyot)), periyot)}
        </span>
      </p>
      <table className="mt-2 w-full min-w-[560px] text-[13px]">
        <thead>
          <tr className="border-b border-line text-left text-[12px] text-muted">
            <th scope="col" className="py-1 pr-3 font-medium">
              {M.donemsel.seciliGun.varlik}
            </th>
            <th scope="col" className="py-1 pr-3 font-medium">
              {M.donemsel.seciliGun.sutunBasi}
            </th>
            <th scope="col" className="py-1 pr-3 font-medium">
              {M.donemsel.seciliGun.sutunSonu}
            </th>
            <th scope="col" className="py-1 text-right font-medium">
              {M.donemsel.seciliGun.getiri}
            </th>
          </tr>
        </thead>
        <tbody>
          {cizgiler.map((c) => {
            const v = c.seri.getiriler[indeks] ?? null;
            const u = c.seri.uclar[indeks];
            return (
              <tr key={c.kimlik} className="border-b border-line align-top last:border-0">
                <th scope="row" className="py-1.5 pr-3 text-left font-normal text-muted">
                  <span className="inline-flex items-center gap-2">
                    <Isaret c={c} />
                    {c.uzunAd}
                  </span>
                </th>
                <td className="py-1.5 pr-3">
                  <UcDegeri u={u?.baslangic ?? null} para={para} aracPara={c.seri.paraBirimi} />
                </td>
                <td className="py-1.5 pr-3">
                  <UcDegeri u={u?.bitis ?? null} para={para} aracPara={c.seri.paraBirimi} />
                </td>
                <td className="py-1.5 text-right">
                  {v === null ? (
                    <Yokluk yokluk={boslukSebebi(c.seri, gun)} />
                  ) : (
                    <span className="pf-num text-ink">{formatRatio(v)}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {cizgiler.some((c) => c.kimlik === "sepet") && (
        <p className="mt-2 text-[11px] text-faint">{M.donemsel.seciliGun.sepetEndeks}</p>
      )}
    </section>
  );
}

function Yokluk({ yokluk }: { readonly yokluk: Absence | undefined }) {
  const olculemedi = yokluk === undefined || yokluk.kind === "olculemedi";
  return (
    <span className={cx("block text-[12px]", olculemedi ? "text-warn" : "text-faint")}>
      — <span className="text-[11px]">{motorMetniVeya(yokluk?.reason, M.donemsel.motorSoylemedi)}</span>
    </span>
  );
}

function Isaret({ c }: { readonly c: Cizgi }) {
  return (
    <svg width="18" height="6" aria-hidden="true" className="shrink-0">
      <line
        x1="0"
        y1="3"
        x2="18"
        y2="3"
        stroke={c.renk}
        strokeWidth={2.5}
        strokeDasharray={c.kesikli ? "4 3" : undefined}
      />
    </svg>
  );
}

function OzetTablosu({ cizgiler, periyot }: { readonly cizgiler: readonly Cizgi[]; readonly periyot: number }) {
  const tarihli = (n: { date: IsoDate; getiri: string } | null) =>
    n === null ? (
      "—"
    ) : (
      <>
        <span className="pf-num">{formatRatio(n.getiri)}</span>
        <span className="ml-1 text-[11px] text-faint">{formatIsoDate(n.date)}</span>
      </>
    );
  const notlar = cizgiler.flatMap((c) => c.seri.notlar.map((n) => ({ c, n })));
  return (
    <section aria-label={M.donemsel.ozet.bolge} className="pf-card overflow-x-auto" data-kart-bolum="ozet">
      <table className="w-full min-w-[640px] text-[13px]">
        <caption className="px-3 pt-3 text-left text-[13px] font-semibold text-ink">
          {M.donemsel.ozet.baslik(periyot)}
        </caption>
        <thead>
          <tr className="border-b border-line text-left text-[12px] text-muted">
            <th scope="col" className="px-3 py-2 font-medium">{M.donemsel.ozet.varlik}</th>
            <th scope="col" className="px-3 py-2 font-medium">{M.donemsel.ozet.son}</th>
            <th scope="col" className="px-3 py-2 font-medium">{M.donemsel.ozet.enDusuk}</th>
            <th scope="col" className="px-3 py-2 font-medium">{M.donemsel.ozet.enYuksek}</th>
            <th scope="col" className="px-3 py-2 text-right font-medium">{M.donemsel.ozet.ortalama}</th>
            <th scope="col" className="px-3 py-2 text-right font-medium">{M.donemsel.ozet.olculenGun}</th>
          </tr>
        </thead>
        <tbody>
          {cizgiler.map((c) => (
            <tr key={c.kimlik} className="border-b border-line last:border-0">
              <th scope="row" className="px-3 py-2 text-left font-medium text-ink">
                <span className="inline-flex items-center gap-2">
                  <Isaret c={c} />
                  {c.ad}
                </span>
              </th>
              <td className="px-3 py-2">{tarihli(c.seri.ozet.son)}</td>
              <td className="px-3 py-2">{tarihli(c.seri.ozet.enDusuk)}</td>
              <td className="px-3 py-2">{tarihli(c.seri.ozet.enYuksek)}</td>
              <td className="pf-num px-3 py-2 text-right">{formatRatio(c.seri.ozet.ortalama)}</td>
              <td className="pf-num px-3 py-2 text-right">{c.seri.ozet.olculenGun}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {notlar.length > 0 && (
        <ul className="space-y-1 border-t border-line px-3 py-2 text-[12px] text-warn">
          {notlar.map(({ c, n }) => (
            <li key={`${c.kimlik}-${n}`}>
              <span className="font-medium">{c.ad}:</span> {motorMetni(n)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Çizgi grafiği (SVG; dış kütüphane yok — Grafik ekranıyla aynı eksen kuralı)
// ---------------------------------------------------------------------------

const SOL = 56;
const SAG = 12;
const UST = 10;
const ALT = 22;

function AnalizGrafigi({
  baslik,
  cizgiler,
  gunler,
  kapali,
  onDegistir,
  secili,
  onSec,
  periyot,
  para,
  yakin,
}: {
  readonly baslik: string;
  readonly cizgiler: readonly Cizgi[];
  readonly gunler: readonly IsoDate[];
  readonly kapali: ReadonlySet<string>;
  readonly onDegistir: (kimlik: string) => void;
  readonly secili: number;
  readonly onSec: (i: number | null) => void;
  readonly periyot: number;
  readonly para: string;
  readonly yakin: Yakinlastirma;
}) {
  // Bilgisayarda %30 daha uzun (kullanıcı, 13.10.2026); telefonda Günlük
  // Analiz ile aynı.
  const yukseklik = darEkranMi() ? YUKSEKLIK : Math.round(YUKSEKLIK * 1.3);
  // İmleç grafiğin üzerindeyken noktanın yanında bilgi kutusu.
  const [uzerinde, setUzerinde] = useState(false);
  const kapRef = useRef<HTMLDivElement | null>(null);
  const [genislik, setGenislik] = useState(720);
  useEffect(() => {
    const el = kapRef.current;
    if (el === null) return;
    const olc = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setGenislik(Math.round(w));
    };
    olc();
    const ro = new ResizeObserver(olc);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const acik = cizgiler.filter((c) => !kapali.has(c.kimlik));
  // YAKINLAŞTIRMA (15.10.2026): yalnızca `bas`..`son` günleri çizilir.
  const { bas, son } = yakin.pencere;
  const n = Math.max(son - bas + 1, 2);
  const icGenislik = Math.max(genislik - SOL - SAG, 10);
  const icYukseklik = yukseklik - UST - ALT;
  // Sürükleyerek aralık seçimi (yalnızca fare).
  const [surukle, setSurukle] = useState<{ readonly a: number; readonly b: number } | null>(null);

  const { min, max, adim } = useMemo(() => {
    let lo = 0;
    let hi = 0;
    for (const c of acik) {
      for (const v of c.seri.getiriler.slice(bas, son + 1)) {
        if (v === null) continue;
        const x = Number(v);
        if (x < lo) lo = x;
        if (x > hi) hi = x;
      }
    }
    if (lo === hi) {
      lo -= 0.1;
      hi += 0.1;
    }
    const a = guzelAdim(hi - lo);
    return { min: Math.floor(lo / a) * a, max: Math.ceil(hi / a) * a, adim: a };
  }, [acik, bas, son]);

  const x = (i: number) => SOL + (icGenislik * (i - bas)) / (n - 1);
  const gorunur = (i: number) => i >= bas && i <= son;
  const y = (v: number) => UST + icYukseklik - ((v - min) / (max - min)) * icYukseklik;

  // Yokluk çizgiyi KESER (sıfır çizilmez). Sepet en üstte çizilsin diye sonda.
  const yollar = acik.map((c) => {
    let d = "";
    let kalem = false;
    for (let i = bas; i <= son; i += 1) {
      const v = c.seri.getiriler[i] ?? null;
      if (v === null) {
        kalem = false;
        continue;
      }
      d += `${kalem ? "L" : "M"}${x(i).toFixed(1)},${y(Number(v)).toFixed(1)}`;
      kalem = true;
    }
    return { c, d };
  });

  const kilavuz = kilavuzDegerleri(min, max, adim);
  const aylar: { i: number; etiket: string }[] = [];
  gunler.forEach((g, i) => {
    if (gorunur(i) && g.endsWith("-01")) aylar.push({ i, etiket: `${g.slice(5, 7)}.${g.slice(2, 4)}` });
  });
  const aySeyrek = Math.max(1, Math.ceil(aylar.length / Math.max(2, Math.floor(icGenislik / 70))));

  const indeksOf = (clientX: number, svg: SVGSVGElement) => {
    const r = svg.getBoundingClientRect();
    const i = bas + Math.round(((clientX - r.left - SOL) / icGenislik) * (n - 1));
    return Math.min(Math.max(i, bas), Math.min(son, gunler.length - 1));
  };
  const merkez = gorunur(secili) ? secili : Math.round((bas + son) / 2);
  const onKlavye = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key === "ArrowLeft") onSec(Math.max(bas, secili - 1));
    else if (e.key === "ArrowRight") onSec(Math.min(son, secili + 1));
    else if (e.key === "Home") onSec(bas);
    else if (e.key === "End") onSec(son);
    else if (e.key === "+" || e.key === "=") yakin.yakinlas(merkez);
    else if (e.key === "-") yakin.uzaklas(merkez);
    else if (e.key === "0") yakin.sifirla();
    else return;
    e.preventDefault();
  };

  return (
    <section aria-label={baslik} className="pf-card p-3" data-kart-bolum="grafik">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-[13px] font-semibold text-ink">{baslik}</h2>
          <YakinlastirmaDugmeleri y={yakin} merkez={merkez} baslik={baslik} />
        </div>
        <div role="group" aria-label={M.grafik.cizgi.cizgiler(baslik)} className="flex flex-wrap gap-1.5">
          {cizgiler.map((c) => {
            const gorunur = !kapali.has(c.kimlik);
            return (
              <button
                key={c.kimlik}
                type="button"
                aria-pressed={gorunur}
                onClick={() => onDegistir(c.kimlik)}
                className={cx(
                  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]",
                  gorunur ? "border-line text-ink" : "border-dashed border-line text-faint",
                )}
              >
                <span style={{ opacity: gorunur ? 1 : 0.35 }}>
                  <Isaret c={c} />
                </span>
                {c.ad}
              </button>
            );
          })}
        </div>
      </div>
      <div ref={kapRef} className="relative mt-2 w-full">
        <svg
          width={genislik}
          height={yukseklik}
          viewBox={`0 0 ${genislik} ${yukseklik}`}
          style={{ maxWidth: "100%", height: "auto" }}
          role="img"
          aria-label={`${M.grafik.cizgi.svgGun(baslik, gunler.length)}${yakin.tam ? "" : M.grafik.cizgi.svgGorunen(n)}${M.grafik.cizgi.svgYardim}`}
          tabIndex={0}
          className="block touch-pan-y outline-none select-none focus-visible:ring-2 focus-visible:ring-[var(--pf-focus)]"
          onPointerMove={(e) => {
            setUzerinde(true);
            const i = indeksOf(e.clientX, e.currentTarget);
            onSec(i);
            if (surukle !== null) setSurukle({ a: surukle.a, b: i });
          }}
          onPointerLeave={() => {
            setUzerinde(false);
            setSurukle(null);
          }}
          onPointerDown={(e) => {
            const i = indeksOf(e.clientX, e.currentTarget);
            onSec(i);
            if (e.pointerType === "mouse" && e.button === 0) setSurukle({ a: i, b: i });
          }}
          onPointerUp={() => {
            if (surukle !== null) yakin.araligaGit(surukle.a, surukle.b);
            setSurukle(null);
          }}
          onDoubleClick={yakin.sifirla}
          onKeyDown={onKlavye}
        >
          {kilavuz.map((v) => (
            <g key={v}>
              <line
                x1={SOL}
                x2={SOL + icGenislik}
                y1={y(v)}
                y2={y(v)}
                stroke="var(--pf-border)"
                strokeWidth={Math.abs(v) < adim / 1e6 ? 1.5 : 1}
              />
              <text x={SOL - 6} y={y(v) + 3.5} textAnchor="end" fontSize="10" fill="var(--pf-text-muted)">
                {M.bicim.yuzde((v * 100).toLocaleString(YEREL, { maximumFractionDigits: 1 }))}
              </text>
            </g>
          ))}
          {aylar
            .filter((_, k) => k % aySeyrek === 0)
            .map((a) => (
              <text key={a.i} x={x(a.i)} y={yukseklik - 6} textAnchor="middle" fontSize="10" fill="var(--pf-text-muted)">
                {a.etiket}
              </text>
            ))}
          {yollar.map(({ c, d }) => (
            <path
              key={c.kimlik}
              data-cizgi={c.kimlik}
              d={d}
              fill="none"
              stroke={c.renk}
              strokeWidth={1.6}
              strokeLinejoin="round"
              strokeDasharray={c.kesikli ? "5 4" : undefined}
            />
          ))}
          {surukle !== null && surukle.a !== surukle.b && (
            <rect
              data-secim="yakinlastirma"
              x={Math.min(x(surukle.a), x(surukle.b))}
              y={UST}
              width={Math.abs(x(surukle.b) - x(surukle.a))}
              height={icYukseklik}
              fill="var(--pf-accent)"
              opacity={0.12}
            />
          )}
          {/* Sıçrama işaretleri: üst kenarda, çizginin renginde üçgen. */}
          {acik.flatMap((c) =>
            c.seri.sicramalar
              .map((sx) => ({ sx, i: gunFarki(gunler[0] ?? sx.date, sx.date) }))
              .filter(({ i }) => i >= 0 && i < gunler.length && gorunur(i))
              .map(({ sx, i }) => (
                <path
                  key={`${c.kimlik}-${sx.date}`}
                  data-sicrama={`${c.kimlik} ${sx.date}`}
                  d={`M${(x(i) - 5).toFixed(1)},${UST} L${(x(i) + 5).toFixed(1)},${UST} L${x(i).toFixed(1)},${UST + 8} Z`}
                  fill={c.renk}
                >
                  <title>{M.donemsel.sicrama.isaretBalon(c.ad, formatIsoDate(sx.date))}</title>
                </path>
              )),
          )}
          {gunler[secili] !== undefined && gorunur(secili) && (
            <g>
              <line
                x1={x(secili)}
                x2={x(secili)}
                y1={UST}
                y2={UST + icYukseklik}
                stroke="var(--pf-text-muted)"
                strokeWidth="1"
                strokeDasharray="2 3"
              />
              {acik.map((c) => {
                const v = c.seri.getiriler[secili] ?? null;
                return v === null ? null : <circle key={c.kimlik} cx={x(secili)} cy={y(Number(v))} r="3.5" fill={c.renk} />;
              })}
            </g>
          )}
        </svg>
        {uzerinde && surukle === null && gunler[secili] !== undefined && gorunur(secili) && (
          <BilgiKutusu
            gun={gunler[secili]}
            indeks={secili}
            cizgiler={acik}
            periyot={periyot}
            para={para}
            x={x(secili)}
            sagda={x(secili) < genislik / 2}
          />
        )}
      </div>
    </section>
  );
}

/**
 * Üzerine gelinen noktanın bilgi kutusu: periyot sonu ve başı, her çizginin
 * baş ve son değeri ve getirisi. Fareyi engellemez; noktanın boş tarafında.
 */
function BilgiKutusu({
  gun,
  indeks,
  cizgiler,
  periyot,
  para,
  x,
  sagda,
}: {
  readonly gun: IsoDate;
  readonly indeks: number;
  readonly cizgiler: readonly Cizgi[];
  readonly periyot: number;
  readonly para: string;
  readonly x: number;
  readonly sagda: boolean;
}) {
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute top-2 z-10 max-w-[22rem] rounded-[var(--pf-radius-sm)] border border-line bg-surface p-2 text-[11px] shadow-md"
      style={sagda ? { left: x + 12 } : { right: `calc(100% - ${x - 12}px)` }}
    >
      <p className="font-semibold text-ink">{formatIsoDate(gun)}</p>
      <p className="text-muted">
        {M.donemsel.bilgiKutusu.periyotBasi(formatIsoDate(gunEkle(gun, -periyot)), para)}
      </p>
      <table className="mt-1">
        <tbody>
          {getiriyeGore(cizgiler, indeks).map((c) => {
            const u = c.seri.uclar[indeks];
            const v = c.seri.getiriler[indeks] ?? null;
            return (
              <tr key={c.kimlik}>
                <th scope="row" className="pr-2 text-left font-normal text-muted">
                  <span className="inline-flex items-center gap-1">
                    <Isaret c={c} />
                    {c.ad}
                  </span>
                </th>
                <td className="pf-num pr-2 text-right text-ink">
                  {u?.baslangic == null ? "—" : sayiYaz(u.baslangic.deger)} →{" "}
                  {u?.bitis == null ? "—" : sayiYaz(u.bitis.deger)}
                </td>
                <td className="pf-num text-right font-semibold text-ink">{v === null ? "—" : formatRatio(v)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Bilgi kutusunun sırası: o günün getirisine göre büyükten küçüğe, her
 * noktada yeniden (kullanıcı, 14.10.2026). Getirisi olmayan en altta.
 * Sayıya çevirmek yalnızca SIRA için — gösterilen değer motorun metni.
 */
export function getiriyeGore<T extends { readonly seri: { readonly getiriler: readonly (string | null)[] } }>(
  cizgiler: readonly T[],
  indeks: number,
): readonly T[] {
  const deger = (c: T): number | null => {
    const v = c.seri.getiriler[indeks] ?? null;
    return v === null ? null : Number(v);
  };
  return [...cizgiler].sort((a, b) => {
    const va = deger(a);
    const vb = deger(b);
    if (va === null || vb === null) return va === null ? (vb === null ? 0 : 1) : -1;
    return vb - va;
  });
}

/** Testler için: istek kurma kuralı. */
export const istekKurTesti = istekKur;
